import classNames from "classnames";
import {ProductService} from "~/AddToCart/ProductService";
import {useMemo} from "react";
import {useIsMinDesktopSize} from "~/common/hooks/useMediaQuery";
import {
  type AllScreenSizes,
  SIZE_BELOW_PHONE,
  SIZE_DESKTOP_L,
  SIZE_DESKTOP_M,
  SIZE_DESKTOP_S,
  SIZE_PHONE,
  SIZE_TABLET,
  useNumericScreenSize,
} from "~/common/hooks/useNumericScreenSize";
import {
  PreferredProductTile
} from "~/ProductDetails/components/PhaseInOut/PreferredProducts/components/PreferredProductTile/PreferredProductTile";
import {useProductContext} from "~/feature/Product/ProductContext";
import {Link} from "~/common/components/Link";
import {PREFERRED_PRODUCTS_ID} from "~/ProductDetails/constants";
import {type ProductData} from "~/models/ProductData";

import classes from "./PreferredProducts.module.less";
import {type PreferredProductCodesContext} from "~/ProductDetails/components/PhaseInOut/usePhaseInOutLogic";
import CenterContents from "~/common/components/CenterContents";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import {useTranslation} from "react-i18next";
import {getInitialPriceReference} from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

interface WrapperProps {
  className?: string;
  data: PreferredProductCodesContext;
}

export const PreferredProducts = ({ data, ...rest }: WrapperProps) => {
  const productsMap = ProductService.useMultipleProductDescriptors({
    productCodes: data.codes,
    priceReference: getInitialPriceReference(),
  });
  const mainProduct = useProductContext();
  const products = useMemo(() => {
    return data.codes.map((code) => productsMap[code]);
  }, [productsMap, data.codes]);

  if (data.status === "loading") {
    return (
      <CenterContents className={classNames(classes.root, rest.className)}>
        <CircleSpinner />
      </CenterContents>
    );
  }

  if (data.status === "loaded") {
    return (
      <PreferredProductsInner
        {...rest}
        preferredProducts={products}
        mainProduct={mainProduct}
        totalPreferredAmount={data.totalAmount}
      />
    );
  }

  return null;
};

interface Props {
  className?: string;
  preferredProducts: ProductDescriptor[];
  mainProduct: ProductData;
  totalPreferredAmount: number;
}

export const PreferredProductsInner = (props: Props) => {
  const { t } = useTranslation();
  const isMinDesktop = useIsMinDesktopSize();
  const showMoreThreshold = isMinDesktop ? 3 : 2;
  const showMore = props.totalPreferredAmount > showMoreThreshold;

  const screenSize = useNumericScreenSize();
  const tileAmount = TILE_AMOUNTS[screenSize];

  const designation = props.mainProduct.designation;

  return (
    <div
      className={classNames(classes.root, props.className)}
      tracking-aux-ppr-parent-product-name={designation}
      tracking-aux-ppr-parent-product-id={props.mainProduct.code}
    >
      <div className={classes.titleRow}>
        <h2 className={classes.title}>{t("pdp.preferredProducts.title", { code: designation })}</h2>
        {showMore && (
          <Link tracking-aux-ppr-show-more="true" href={"#" + PREFERRED_PRODUCTS_ID}>
            {t("page.pdp.phaseinout.preferred.showmore")}
          </Link>
        )}
      </div>
      <div className={classes.tiles}>
        {props.preferredProducts.slice(0, tileAmount).map((product) => (
          <PreferredProductTile key={product.base.code} className={classes.tile} product={product} />
        ))}
      </div>
    </div>
  );
};

const TILE_AMOUNTS: Record<AllScreenSizes, number> = {
  [SIZE_BELOW_PHONE]: 1,
  [SIZE_PHONE]: 1,
  [SIZE_TABLET]: 1,
  [SIZE_DESKTOP_S]: 2,
  [SIZE_DESKTOP_M]: 2,
  [SIZE_DESKTOP_L]: 3,
};
